import * as React from 'react';
import { format } from 'date-fns';
import type { CaptionProps, DayPickerMultipleProps, DayPickerRangeProps, DayPickerSingleProps } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import DropdownPopup from '../DropdownPopup/DropdownPopup';
import CalendarCaption from './CalendarCaption';
import cn from '~modules/cn';
import 'react-day-picker/dist/style.css';
import './calendar-input.scss';

type BaseProps = {
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    fromDate?: Date;
    toDate?: Date;
    inputClassName?: string;
    tooltipClassName?: string;
};

type SpecificProps =
    | {
          mode: 'single';
          value?: Date;
          onChange?: { (date: Date): void };
      }
    | {
          mode: 'multiple';
          value?: Date[];
          onChange?: { (dates: Date[]): void };
      }
    | {
          mode: 'range';
          value?: {
              from: Date;
              to?: Date;
          };
          onChange?: { (dates: { from?: Date; to?: Date }): void };
      };

export type Props = BaseProps & SpecificProps;

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

const getInputText = (props: Props): string => {
    const { dateFormat = DEFAULT_DATE_FORMAT } = props;
    if (!props.value) {
        return props.placeholder ?? '';
    }
    if (props.mode === 'single') {
        return format(props.value, dateFormat);
    }
    if (props.mode === 'multiple') {
        return props.value.map((date) => format(date, dateFormat)).join(', ');
    }
    if (props.mode === 'range') {
        return [props.value.from, props.value.to]
            .filter(Boolean)
            .map((date) => format(date!, dateFormat))
            .join(' - ');
    }
    return '';
};

const CalendarInput = (props: Props) => {
    const { dateFormat = DEFAULT_DATE_FORMAT } = props;

    const CaptionComponent = React.useMemo(() => {
        const CalendarCaptionWrapper = (innerProps: CaptionProps) => <CalendarCaption {...innerProps} dateFormat={dateFormat} />;
        return CalendarCaptionWrapper;
    }, [dateFormat]);

    let compProps;
    if (props.mode === 'single') {
        compProps = {
            mode: props.mode,
            selected: props.value,
            defaultMonth: props.value,
            onSelect: (date) => date && props.onChange?.(date)
        } as DayPickerSingleProps;
    } else if (props.mode === 'multiple') {
        compProps = {
            mode: props.mode,
            selected: props.value,
            defaultMonth: props.value?.[0],
            onSelect: (dates) => dates && props.onChange?.(dates)
        } as DayPickerMultipleProps;
    } else if (props.mode === 'range') {
        compProps = {
            mode: props.mode,
            selected: props.value,
            min: 2,
            defaultMonth: props.value?.from,
            onSelect: (dates) => dates && props.onChange?.(dates)
        } as DayPickerRangeProps;
    }

    return (
        <DropdownPopup
            buttonProps={{
                className: cn('calendar-input', props.inputClassName, { 'calendar-input--placeholder': !props.value }),
                disabled: props.disabled,
                children: getInputText(props)
            }}
            tooltipClassName={cn('calendar-input-tooltip', props.tooltipClassName)}
        >
            <DayPicker
                {...compProps}
                components={{
                    Caption: CaptionComponent
                }}
                fromDate={props.fromDate}
                toDate={props.toDate}
            />
        </DropdownPopup>
    );
};

export default CalendarInput;
